import _, { get, isFunction, merge } from "lodash";
import ecma from "tern/defs/ecmascript.json";

import {
  ApiResponseType,
  ApplicationScope,
  WidgetTypes,
} from "@superblocksteam/shared";

import { convertDef } from "autocomplete/convertPublicDefs";
import { AutocompleteFunctionsType } from "components/app/CodeEditor/EditorConfig";
import type { AutocompleteConfiguration } from "components/app/CodeEditor/EditorConfig";
import omit from "core/utils/omit";
import type {
  DataTree,
  DataTreeAction,
  DataTreeEmbed,
  DataTreeEmbedProp,
  DataTreeEntity,
  DataTreeEvent,
  DataTreeStateVar,
  DataTreeTimer,
  DataTreeWidget,
} from "legacy/entities/DataTree/dataTreeFactory";
import type { ExecutionOutputDto } from "store/slices/apis/types";
import { ENTITY_TYPE } from "utils/dataTree/constants";
import { omitGlobal } from "utils/dataTree/globalOmit";
import { resolveScopes } from "utils/dataTree/resolveScopes";
import type { ApiScope } from "utils/dataTree/scope";
import { getDottedPathTo } from "utils/dottedPaths";

import recursiveGenerateTypeDef from "./recursiveGenerateTypeDef";
import type { EntityType, TypeDef, TypeInfo } from "./types";

export enum Origin {
  ENTITY_AC = "entity-complete",
  CUSTOM_DATA_TREE = "customDataTree",
  DATA_TREE = "dataTree",
  APP_DATA_TREE = "appDataTree",
}

export type DataTreeDef = TypeInfo & {
  "!name"?: string;
  Global?: any;
  theme?: any;
  secrets?: any;
  icons?: any;
};

/**
 * We use this function because we want [fdafssda] to take precedence over
 * inline docs for a smaller type def.
 * This is also protects the worker which will fail to correctly parse string objects
 * that also contain other values. It will drop the extra values and cause tern to fail
 * TODO: The Global object needs to be cleaned up as the use
 * of this is due to poor typeDefDesign.
 */
const mergeUnlessString = (a: any, b: any) => {
  const firstIsString = _.isString(a);
  const secondIsString = _.isString(b);
  // if both are strings, return the first one
  if (firstIsString && secondIsString) {
    return a;
  }
  // if one is a string and the other is an object, return the string
  if (firstIsString || secondIsString) {
    return firstIsString ? a : b;
  }
  // if both are objects, merge them
  return merge(a, b);
};

export const entityDefinitions = {
  ACTION: (
    entity: DataTreeAction,
    functions?: AutocompleteFunctionsType | false,
  ) => {
    let response: undefined | Record<string, any>;
    if (entity.responseType !== ApiResponseType.STREAM) {
      const dataDef = generateTypeDef(entity.response);
      response = {
        "!doc": "The output of the last step of the API",
      };
      if (_.isString(dataDef)) {
        response["!type"] = dataDef;
      } else {
        response = { ...response, ...dataDef };
      }
    }

    return {
      "!doc":
        "Superblocks APIs are used to connect components to data from database queries, API calls and business logic in JS",
      "!url": "",
      ...(response ? { response } : {}),
      error: {
        "!type": "string | null",
        "!doc":
          "The API's error message. If there is no error, this will be null.",
        "!url": "",
      },
      ...(functions === AutocompleteFunctionsType.FRONTEND
        ? {
            "run()": {
              "!type": "fn() -> void",
              "!doc":
                "Function used to call a Superblocks API from a UI Component or API step",
              "!url": "",
            },
            "clearResponse()": {
              "!type": "fn() -> void",
              "!doc":
                "Clear the response from the last call of the API. For example, clear when the input text changes.",
              "!url": "",
            },
            "cancel()": {
              "!type": "fn() -> void",
              "!doc":
                "Function used to cancel a running Superblocks API from a UI Component or API step",
              "!url": "",
            },
          }
        : {}),
    };
  },
  ACTION_STEP: (entity?: ExecutionOutputDto) => {
    const dataDef = generateTypeDef(entity?.output || undefined);

    let outputDef: Record<string, any> = {
      "!doc": "The output of a single step of the API",
    };
    if (_.isString(dataDef)) {
      outputDef["!type"] = dataDef;
    } else {
      outputDef = { ...outputDef, ...dataDef };
    }
    return {
      "!doc": "One of the previous steps of the API",
      output: outputDef,
    };
  },
  STATE_VAR: (
    entity: DataTreeStateVar,
    functions?: AutocompleteFunctionsType | false,
    data?: any,
  ) => {
    return {
      "!doc":
        "Superblocks Variables are used to store & read data in your app.",
      "!url": "",
      value: {
        "!type": generateTypeDef(data?.value ?? {}),
        "!doc": "The value of the state",
        "!url": "",
      },
      ...(functions === AutocompleteFunctionsType.FRONTEND
        ? {
            "set()": {
              "!type": "fn(data: any) -> void",
              "!doc": "sets the value for this variable",
            },
            "setProperty()": {
              "!type": "fn(path: string, data: any) -> void",
              "!doc":
                "assumes that the variable is an object and sets the value for a property path (e.g. name.first) in the variable",
            },
            "resetToDefault()": {
              "!type": "fn() -> void",
              "!doc": "resets the variable to its default value",
            },
          }
        : {}),
    };
  },
  EMBED: (
    entity: DataTreeEmbed,
    functions?: AutocompleteFunctionsType | false,
  ) => {
    return {
      "!doc":
        "Embed object with data passed from host app to Superblocks via iframe",
      "!url": "",
    };
  },
  EMBED_PROP: (
    entity: DataTreeEmbedProp,
    functions?: AutocompleteFunctionsType | false,
  ) => {
    return {
      "!doc": "Embed property passed from host app to Superblocks via iframe",
      "!url": "",
      value: {
        "!type": "any",
        "!doc":
          "The value of the embed property passed from host app to Superblocks app",
        "!url": "",
      },
    };
  },
  CUSTOM_EVENT: (
    entity: DataTreeEvent,
    functions?: AutocompleteFunctionsType | false,
  ) => {
    const argsType =
      entity.arguments && entity.arguments.length > 0
        ? `{${(entity.arguments ?? []).map((a) => `${a.name}: ?`).join(", ")}}`
        : "{}";
    return {
      "!doc":
        "Custom events are used to trigger actions in your app based on user interactions",
      "!url": "",
      ...(functions === AutocompleteFunctionsType.FRONTEND
        ? {
            "trigger()": {
              "!type": `fn(payload: ${argsType}) -> void`,
              "!doc":
                "Trigger this custom event. The payload maps argument names to values.",
            },
          }
        : {}),
    };
  },
  TIMER: (
    entity: DataTreeTimer,
    functions?: AutocompleteFunctionsType | false,
  ) => {
    return {
      "!doc":
        "Superblocks Timers are used to call APIs or widget actions periodically on an interval",
      "!url": "",
      isActive: {
        "!type": "bool",
        "!doc": "Whether or not the timer is running",
        "!url": "",
      },
      ...(functions === AutocompleteFunctionsType.FRONTEND
        ? {
            "start()": {
              "!type": "fn() -> void",
              "!doc": "start this timer",
            },
            "stop()": {
              "!type": "fn() -> void",
              "!doc": "stop this timer",
            },
            "toggle()": {
              "!type": "fn() -> void",
              "!doc": "toggle this timer on or off",
            },
          }
        : {}),
    };
  },
};
const GLOBAL_DEFS = {
  dropdownOption: {
    label: "string",
    value: "string",
  },
  tabs: {
    id: "string",
    label: "string",
  },
  chartDataPoint: {
    x: "string",
    y: "string",
  },
  chartData: {
    seriesName: "string",
    data: "[chartDataPoint]",
  },
  latLong: {
    lat: "number",
    long: "number",
  },
  mapMarker: {
    lat: "number",
    long: "number",
    title: "string",
    description: "string",
  },
  diffChange: {
    value: "string",
    added: "bool",
    removed: "bool",
    count: "number",
  },
  Map: convertDef(ecma.Map),
  Set: convertDef(ecma.Set),
};
export const GLOBAL_FUNCTIONS: Record<string, any> = {
  "navigateTo()": {
    "!doc": {
      docString: `Navigate to URL or route in the app. For example: navigateTo("https://google.com", {}, "NEW_WINDOW") or navigateTo("/route", { showFilter: true })`,
    },
    "!type": `fn(urlOrRoute: string, queryParams: {}, target?: string) -> void`,
  },
  "setQueryParams()": {
    "!doc": {
      expanded: {
        markdownDoc:
          'Update the query parameters without refreshing the page or running page load APIs. For example: `setQueryParams({ "search": userInput.value })`',
      },
    },
    "!type": `fn(queryParams: {}, preserveExistingQueryParams?: boolean) -> void`,
  },
  "showAlert()": {
    "!doc": {
      docString: "Show a temporary notification style message to the user",
    },
    "!type":
      "fn(message: string, style: string, durationSeconds?: number, alertPosition?: string) -> void",
  },
  "download()": {
    "!doc": { docString: "Download anything as a file" },
    "!type": "fn(data: any, fileName?: string, fileType?: string) -> void",
  },
  "copyToClipboard()": {
    "!doc": { docString: "Copy text to clipboard" },
    "!type": "fn(data: string, options: object) -> void",
  },
  "resetComponent()": {
    "!doc": { docString: "Reset component to its initial state" },
    "!type": "fn(componentName: string, resetChildren: bool) -> void",
  },
  "logoutIntegrations()": {
    "!doc": {
      docString:
        "Logs out the user from authenticated integrations used in APIs",
    },
    "!type": "fn() -> void",
  },
};
export const entityDefinitionMetadata: Record<string, any> = {
  ACTION: {
    sortOrder: {
      response: 1,
      error: 2,
    },
  },
  TIMER: {},
  GLOBAL: {
    sortOrder: {
      URL: 1,
    },
  },
  "GLOBAL.URL": {
    sortOrder: {
      routeParams: 1,
      queryParams: 2,
    },
  },
};

let extraDefs: TypeInfo = {};
// Until autocomplete definition loading is made lazy, limit the amount of work we do on a single object
const MAX_KEYS_TO_AUGMENT = 100;

const augmentDefinitionWithEvaluatedValueHelper = (
  entityType: EntityType,
  obj: any,
  definition: any,
  path: string,
): TypeInfo => {
  if (!_.isObject(definition) || _.isArray(definition)) {
    return {
      "!type": definition,
      "!doc": {
        docString: definition["!doc"] ?? "",
        value: _.get(obj, path),
        entityType,
      },
    };
  }
  const augmentedDefinition = {} as TypeInfo;
  let keysAugmented = 0;
  for (const [field, typedef] of Object.entries(definition)) {
    if (field.startsWith("!")) {
      continue;
    }
    if (keysAugmented > MAX_KEYS_TO_AUGMENT) {
      break;
    }
    augmentedDefinition[field] = augmentDefinitionWithEvaluatedValueHelper(
      entityType,
      obj,
      typedef,
      `${path}${
        /^fn\(/.test(typedef["!type"]) ? field : getDottedPathTo(field)
      }`,
    );
    keysAugmented++;
  }

  const doc = _.get(definition, "!doc");
  const ret: TypeInfo = {
    ...augmentedDefinition,
    "!doc": {
      docString:
        "!doc" in definition && typeof doc === "string" ? (doc as string) : "",
      markdownDocString: _.get(doc, "markdownDocString") ?? undefined,
      value: _.get(obj, path),
      entityType,
    },
  };

  const type = _.get(definition, "!type");
  if (_.isString(type)) {
    ret["!type"] = type;
  } else if (type && _.isObject(type) && !_.isArray(type)) {
    // evaluate definitions of dynamic fields (not in definition)
    keysAugmented = 0;
    for (const [field, typedef] of Object.entries(type)) {
      if (field.startsWith("!")) {
        continue;
      }
      if (keysAugmented > MAX_KEYS_TO_AUGMENT) {
        break;
      }
      ret[field] = augmentDefinitionWithEvaluatedValueHelper(
        entityType,
        obj,
        typedef,
        path + "." + field,
      );
      keysAugmented++;
    }
  }
  return ret;
};

const augmentDefinitionAWithEvaluatedValue = (
  entityType: EntityType,
  obj: any,
  definition: any,
): Record<string, unknown> => {
  const ret: Record<string, unknown> = {};
  ret["!doc"] = {
    docString:
      "!doc" in definition
        ? `${_.get(definition, "!doc")}${
            "!type" in definition ? `\n${_.get(definition, "!type")}` : ""
          }`
        : "",
    value: Object.keys(definition).reduce((acc: any, key: string) => {
      if (obj && !key.startsWith("!")) acc[key] = obj[key];
      return acc;
    }, {}),
    topLevel: true,
    entityType,
  };
  for (const [key, value] of Object.entries(definition)) {
    if (key.startsWith("!")) {
      continue;
    }
    ret[key] = augmentDefinitionWithEvaluatedValueHelper(
      entityType,
      obj,
      value,
      key,
    );
  }
  return ret;
};

export const envTypeDefDocString = {
  "!doc": {
    docString: "Environment variables set from SUPERBLOCKS_AGENT_ENV_VARS_JSON",
    entityType: "GLOBAL",
    value: "...",
  },
};

type TypeDefCreatorParams = {
  dataTree: DataTree;
  appScope: ApplicationScope;
  apiScope?: ApiScope;
  configuration?: AutocompleteConfiguration;
  typeDefName?: string;
};

export const dataTreeTypeDefCreator = ({
  dataTree,
  appScope,
  apiScope,
  configuration,
  typeDefName = Origin.DATA_TREE,
}: TypeDefCreatorParams): DataTreeDef => {
  const def: DataTreeDef = {
    "!name": typeDefName,
  };
  const scopes = resolveScopes(appScope);
  scopes.forEach((scope) => {
    generateEntityDefinitions(
      dataTree[scope.scope],
      scope.prefix,
      scope.hidden,
    );
  });

  def["!define"] = {
    ...GLOBAL_DEFS,
    ...extraDefs,
    ...(def["!define"] ?? {}),
  };
  extraDefs = {};
  if (configuration?.functions === AutocompleteFunctionsType.FRONTEND) {
    return { ...def, ...GLOBAL_FUNCTIONS };
  }
  return def;

  function generateEntityDefinitions(
    data: typeof dataTree.APP | typeof dataTree.PAGE,
    prefix?: string,
    hidden?: boolean,
  ) {
    const dataTreeKeys = Object.keys(data);

    if (dataTreeKeys.length === 0) {
      return;
    }

    let localDef = def;
    if (prefix) {
      localDef = _.get(def, prefix, {
        "!doc": {
          hidden,
        },
      }) as DataTreeDef;
      _.set(def, prefix, localDef);
    }

    dataTreeKeys.forEach((entityName) => {
      const entity = data[entityName];
      if (entity && "ENTITY_TYPE" in entity) {
        switch (entity.ENTITY_TYPE) {
          case ENTITY_TYPE.WIDGET: {
            const widgetType = entity.type;
            if (widgetType in entityDefinitions) {
              const definition = _.get(entityDefinitions, widgetType);

              const augmentedDefinition = augmentDefinitionAWithEvaluatedValue(
                widgetType,
                entity,
                _.isFunction(definition)
                  ? definition(entity, configuration?.functions)
                  : definition,
              );

              // TODO(omar): remove this once we ship programmatic table edit
              if (
                widgetType === WidgetTypes.TABLE_WIDGET &&
                !configuration?.isProgrammaticTableEnabled
              ) {
                delete augmentedDefinition["updateRows()"];
                delete augmentedDefinition["deleteRows()"];
                delete augmentedDefinition["insertRows()"];
              }

              localDef[entityName] = augmentedDefinition;
            }
            break;
          }
          case ENTITY_TYPE.ACTION:
            if (
              !apiScope ||
              configuration?.functions !== AutocompleteFunctionsType.BACKEND
            ) {
              localDef[entityName] = augmentDefinitionAWithEvaluatedValue(
                ENTITY_TYPE.ACTION,
                entity,
                entityDefinitions.ACTION(entity, configuration?.functions),
              );
            } else if (apiScope.apiName === entity.name) {
              for (const actionName of apiScope.previousV1ActionNames) {
                const actionOutput = entity.actionOutputs[actionName] ?? {
                  output: null,
                };
                localDef[actionName] = augmentDefinitionAWithEvaluatedValue(
                  ENTITY_TYPE.ACTION,
                  { output: actionOutput.output },
                  entityDefinitions.ACTION_STEP(actionOutput),
                );
              }
              for (const inScopeVar of Object.keys(
                apiScope.v2ComputedScope ?? {},
              )) {
                // inScopeVar could either be a variable or a block name
                const computedScopeInfo =
                  apiScope.v2ComputedScope?.[inScopeVar];
                if (computedScopeInfo) {
                  localDef[inScopeVar] = computedScopeInfo;
                }
              }
            } else if (apiScope.apiName !== entity.name) {
              localDef[entityName] = augmentDefinitionAWithEvaluatedValue(
                ENTITY_TYPE.ACTION,
                entity,
                entityDefinitions.ACTION(entity, false),
              );
            }
            break;
          case ENTITY_TYPE.STATE_VAR:
            localDef[entityName] = augmentDefinitionAWithEvaluatedValue(
              ENTITY_TYPE.STATE_VAR,
              entity,
              entityDefinitions.STATE_VAR(
                entity,
                configuration?.functions,
                data[entityName],
              ),
            );
            break;
          case ENTITY_TYPE.EMBEDDING: {
            let embedPropsDef = {};
            const sanitizedEntity = omit(
              entity,
              "skippedEvaluation",
              "ENTITY_TYPE",
            );
            Object.entries(sanitizedEntity as DataTreeEmbed).forEach(
              ([embedPropName, embedProp]) => {
                if (
                  _.isObject(embedProp) &&
                  (embedProp as DataTreeEmbedProp)?.ENTITY_TYPE !==
                    ENTITY_TYPE.EMBED_PROP
                )
                  return;
                embedPropsDef = {
                  ...embedPropsDef,
                  [embedPropName]: augmentDefinitionAWithEvaluatedValue(
                    ENTITY_TYPE.EMBED_PROP,
                    embedProp,
                    entityDefinitions.EMBED_PROP(
                      embedProp,
                      configuration?.functions,
                    ),
                  ),
                };
              },
            );
            localDef.Embed = {
              "!doc": {
                docString: entityDefinitions.EMBED(entity)["!doc"],
                value: sanitizedEntity,
                entityType: "EMBEDDING",
              },
              ...embedPropsDef,
            };
            break;
          }
          case ENTITY_TYPE.TIMER:
            localDef[entityName] = augmentDefinitionAWithEvaluatedValue(
              ENTITY_TYPE.TIMER,
              entity,
              entityDefinitions.TIMER(entity, configuration?.functions),
            );
            break;
          case ENTITY_TYPE.ICONS:
            if (configuration?.shouldOpenIconSelectorForIconBindings) {
              localDef.icons = {
                "!name": "icons",
                icons: {},
              };
            }
            break;
          case ENTITY_TYPE.THEME: {
            const sanitizedEntity = omit(
              entity,
              "skippedEvaluation",
              "ENTITY_TYPE",
            );
            // theme + Global will be used in the same places
            if (configuration?.global) {
              localDef.theme = {
                // @ts-expect-error: always going to be an obj
                ...generateTypeDef(
                  omit(
                    sanitizedEntity,
                    "bindingPaths" as any,
                    "evaluatedValues" as any,
                    "invalidProps" as any,
                    "validationMessages" as any,
                  ),
                ),
                "!doc": {
                  docString:
                    "The theme holds information about the color palette, font family, and mode.",
                  value: sanitizedEntity,
                  entityType: "THEME",
                },
              };
              localDef.theme.colors = mergeUnlessString(localDef.theme.colors, {
                "!doc": {
                  docString:
                    "An object containing all of the theme-based colors being used by the application.",
                  value: sanitizedEntity.colors,
                },
                "!type": localDef.theme.colors["!type"],
              });
              localDef.theme.mode = mergeUnlessString(localDef.theme.mode, {
                "!doc": {
                  docString:
                    "Set to LIGHT or DARK, depending on what mode the app is in.",
                  value: sanitizedEntity.mode,
                },
                "!type": localDef.theme.mode["!type"],
              });
              localDef.theme.fontFamily = mergeUnlessString(
                localDef.theme.fontFamily,
                {
                  "!doc": {
                    docString:
                      "The string name of the font family being used by the application.",
                    value: sanitizedEntity.fontFamily,
                  },
                  "!type": localDef.theme.fontFamily["!type"],
                },
              );
            }
            break;
          }
          case ENTITY_TYPE.CUSTOM_EVENT: {
            // only add on the frontend
            if (
              configuration?.functions === AutocompleteFunctionsType.FRONTEND
            ) {
              localDef[entityName] = entityDefinitions.CUSTOM_EVENT(
                entity,
                configuration?.functions,
              );
            }
            break;
          }
          case ENTITY_TYPE.GLOBAL: {
            const sanitizedEntity = omit(
              entity,
              "mode",
              "skippedEvaluation",
              "ENTITY_TYPE",
            );
            if (configuration?.global) {
              localDef.Global = {
                // @ts-expect-error: always going to be an obj
                ...generateTypeDef(omitGlobal(sanitizedEntity)),
                "!doc": {
                  docString:
                    "The Global holds metadata for the application like the URL, user information, etc.",
                  value: sanitizedEntity,
                  entityType: "GLOBAL",
                },
              };
              localDef.Global.URL = mergeUnlessString(localDef.Global.URL, {
                "!doc": {
                  docString:
                    "A string representing the URL of the current application page which the end-user is viewing.",
                  value: sanitizedEntity.URL,
                  entityType: "GLOBAL",
                },
                "!type": localDef.Global.URL["!type"],
              });
              localDef.Global.URL.routeParams = mergeUnlessString(
                localDef.Global.URL.routeParams,
                {
                  "!doc": {
                    docString:
                      "Contains any matches for the current route. For example /:userId would contain a userId key if the route was /1234",
                    value: sanitizedEntity.URL.routeParams,
                    entityType: "GLOBAL.URL",
                  },
                  "!type": localDef.Global.URL.routeParams["!type"],
                },
              );
              localDef.Global.URL.queryParams = mergeUnlessString(
                localDef.Global.URL.queryParams,
                {
                  "!doc": {
                    docString:
                      "Contains the current query parameters in the URL. For example, ?userId=1234 would contain a userId key if the query was userId=1234",
                    value: sanitizedEntity.URL.queryParams,
                    entityType: "GLOBAL.URL",
                  },
                  "!type": localDef.Global.URL.queryParams["!type"],
                },
              );
              localDef.Global.user = mergeUnlessString(localDef.Global.user, {
                "!doc": {
                  docString:
                    "An object describing the end-user who is viewing and running the application.",
                  value: sanitizedEntity.user,
                  entityType: "GLOBAL",
                },
                "!type": localDef.Global.user["!type"],
              });
              localDef.Global.user.groups = mergeUnlessString(
                localDef.Global.user.groups,
                {
                  "!doc": {
                    value: sanitizedEntity.global.user.groups,
                    docString:
                      "An array of objects describing user groups the end-user has joined.",
                  },
                  "!type": localDef.Global.user.groups,
                },
              );
              localDef.Global.groups = mergeUnlessString(
                localDef.Global.user.groups,
                {
                  "!doc": {
                    docString:
                      "An array of objects describing all user groups under your organization.",
                    value: sanitizedEntity.global.groups,
                    entityType: "GLOBAL",
                  },
                  "!type": localDef.Global.groups,
                },
              );
              localDef.Global.createdAt = {
                "!doc": {
                  docString:
                    "The application creation time in ISO 8601 string (UTC)",
                  value: sanitizedEntity.createdAt,
                  entityType: "GLOBAL",
                },
                "!type": "string",
              };
              localDef.Global.deployedAt = {
                "!doc": {
                  docString:
                    "The deployment time of the application's latest version in ISO 8601 string (UTC)",
                  value: sanitizedEntity.deployedAt,
                  entityType: "GLOBAL",
                },
                "!type": "string",
              };
              localDef.Global.versions = mergeUnlessString(
                localDef.Global.versions,
                {
                  "!doc": {
                    docString: "An object describing the application versions",
                    value: sanitizedEntity.global.versions,
                    entityType: "GLOBAL",
                  },
                },
              );
              localDef.Global.versions.current = mergeUnlessString(
                localDef.Global.versions.current,
                {
                  "!doc": {
                    docString:
                      "An object describing the current application version",
                    value: sanitizedEntity.global.versions.current,
                  },
                },
              );
              localDef.Global.versions.current.description = mergeUnlessString(
                localDef.Global.versions.current.description,
                {
                  "!doc": {
                    docString: "A string of deployment description",
                    value: sanitizedEntity.global.versions.current.description,
                  },
                },
              );
              localDef.Global.versions.current.tag = mergeUnlessString(
                localDef.Global.versions.current.tag,
                {
                  "!doc": {
                    docString: "A string of version tag",
                    value: sanitizedEntity.global.versions.current.tag,
                  },
                },
              );
              localDef.Global.versions.current.deployedAt = mergeUnlessString(
                localDef.Global.versions.current.deployedAt,
                {
                  "!doc": {
                    docString:
                      "An ISO string describing the application deployment timestamp",
                    value: sanitizedEntity.global.versions.current.deployedAt,
                  },
                },
              );
              localDef.Global.versions.current.deployedBy = mergeUnlessString(
                localDef.Global.versions.current.deployedBy,
                {
                  "!doc": {
                    docString:
                      "An object describing who deployed the application",
                    value: sanitizedEntity.global.versions.current.deployedBy,
                  },
                },
              );
            }

            if (configuration?.env) {
              localDef.Env = envTypeDefDocString;
            }
            if (configuration?.additionalDefs) {
              for (const [key, value] of Object.entries(
                configuration.additionalDefs,
              )) {
                localDef[key] = value;
              }
            }

            break;
          }
        }
      }
    });
  }
};

/**
 * A type def creator that generates definitions for app scope entities with the `App.` prefix, for example `App.AppVar1`
 * while keeping the original definitions for the entities without the prefix for autocomplete purposes, allowing the user
 * to type AppVar without the `App.` prefix.
 */
export const appScopeDataTreeTypeDefCreator = (
  params: Omit<TypeDefCreatorParams, "typeDefName"> & {
    keepOriginalDef?: boolean;
  },
): DataTreeDef => {
  const typeDef = dataTreeTypeDefCreator({
    ...params,
    typeDefName: Origin.APP_DATA_TREE,
  });
  const entityNames = new Set(
    Object.keys(params.dataTree[ApplicationScope.APP]),
  );

  const keepOriginalDefs = params.keepOriginalDef ?? true;
  for (const [entityName, entityDef] of Object.entries(typeDef)) {
    if (entityNames.has(entityName)) {
      typeDef[`App.${entityName}`] = entityDef;
    }

    if (!keepOriginalDefs) {
      delete typeDef[entityName];
    }
  }

  return typeDef;
};

function generateTypeDef(obj: any): TypeDef {
  return recursiveGenerateTypeDef(obj, extraDefs);
}

/************************************/
/** MAPPING HELPERS
 ** These filter an entity based on the definition
/************************************/

export const accessibleEntityProps = (entity: DataTreeEntity) => {
  if (!("ENTITY_TYPE" in entity)) {
    return;
  }
  return EntityTypeToDefinitionMap[entity.ENTITY_TYPE](entity);
};

const isTypeKeyOfEntityDefinitions = (
  key: string,
): key is keyof typeof entityDefinitions => {
  return key in entityDefinitions;
};

const filterEntityByDefinition = (
  definition: Record<string, unknown> | undefined,
  dataTreeEntity: DataTreeEntity,
) => {
  const ret: Record<string, any> = {};
  if (!definition) {
    return undefined;
  }
  for (const key of Object.keys(definition)) {
    if (key.startsWith("!")) {
      continue;
    }
    ret[key] = get(dataTreeEntity, key);
  }
  return ret;
};

const EntityTypeToDefinitionMap = {
  [ENTITY_TYPE.WIDGET]: (entity_) => {
    const entity = entity_ as DataTreeWidget;
    const type = entity.type;
    const hasDef = isTypeKeyOfEntityDefinitions(type);
    const def = hasDef ? entityDefinitions[type] : undefined;
    const resolved = isFunction(def)
      ? (def as (...args: any[]) => any)(entity)
      : def;
    return filterEntityByDefinition(resolved as any, entity);
  },
  [ENTITY_TYPE.ACTION]: (entity) =>
    filterEntityByDefinition(entityDefinitions.ACTION(entity as any), entity),
  [ENTITY_TYPE.TIMER]: (entity) =>
    filterEntityByDefinition(entityDefinitions.TIMER(entity as any), entity),
  [ENTITY_TYPE.STATE_VAR]: (entity) =>
    filterEntityByDefinition(
      entityDefinitions.STATE_VAR(entity as any),
      entity,
    ),
  [ENTITY_TYPE.EMBEDDING]: (entity) => {
    const result =
      filterEntityByDefinition(
        entityDefinitions.EMBED(entity as any),
        entity,
      ) ?? {};
    Object.entries(entity).forEach(([subEntityName, subEntity]) => {
      if (subEntity?.ENTITY_TYPE === ENTITY_TYPE.EMBED_PROP) {
        result[subEntityName] = filterEntityByDefinition(
          entityDefinitions.EMBED_PROP(subEntity),
          subEntity,
        );
      }
    });
    return result;
  },
  [ENTITY_TYPE.CUSTOM_EVENT]: (entity) =>
    filterEntityByDefinition(
      entityDefinitions.CUSTOM_EVENT(entity as any),
      entity,
    ),
  [ENTITY_TYPE.EMBED_PROP]: (entity) => {
    return undefined;
  },
  [ENTITY_TYPE.GLOBAL]: (entity) => omitGlobal(entity),
  [ENTITY_TYPE.ICONS]: () => undefined,
  // there is an overlap between theme and global props
  [ENTITY_TYPE.THEME]: (entity) => omitGlobal(entity),
  [ENTITY_TYPE.NESTED_ITEM]: () => undefined,
  [ENTITY_TYPE.API]: () => undefined,
} satisfies Record<ENTITY_TYPE, (e: DataTreeEntity) => object | undefined>;
